import React, { useEffect, useState, useMemo } from "react";
import { useErapViolations } from "@/hooks/useErapViolations";
import {
  buildOrgGroupRows,
  buildSubjectTotals,
} from "@/data/adminViolationsFallback";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useApiRequest } from "@/hooks/useApiRequest";
import { TrendingUp, TrendingDown } from "lucide-react";

type PeriodKey = "month" | "quarter" | "year";

type OrgAnalyticsProps = {
  region: string;
  period?: "month" | "quarter" | "year" | string;
};

const ORG_TITLES: Record<string, string> = {
  "1": "Правоохранительные органы",
  "2": "Независимые органы",
  "3": "Агентства",
  "4": "Министерства",
  "5": "Местные исполнительные органы",
};

type Kpi = {
  direction: "up" | "down" | "no-change" | "na";
  percent: number | null;
  absolute: number;
};
type OrgAnalyticsResponse = {
  rows: {
    organization_type: string;
    chosen: Record<string, number>;
    previous: Record<string, number>;
  }[];
};
export const OrgAnalytics: React.FC<OrgAnalyticsProps> = ({
  region,
  period = "month",
}) => {
  const [orgGroupsStats, setOrgGroupsStats] = useState<any[]>([]);
  const [kpis, setKpis] = useState<Record<string, Record<PeriodKey, Kpi>>>(
    {} as any
  );

  const regionsList = [
    { region_id: 24, region_name: "Кызылкогинский район" },
    { region_id: 25, region_name: "Курмангазинский район" },
    { region_id: 26, region_name: "Курмангазинский район" },
    { region_id: 27, region_name: "г. Атырау" },
    { region_id: 28, region_name: "Жылыойский район" },
    { region_id: 29, region_name: "Жылыойский район" },
    { region_id: 30, region_name: "Махамбетский район" },
    { region_id: 31, region_name: "Махамбетский район" },
    { region_id: 32, region_name: "Курмангазинский район" },
    { region_id: 33, region_name: "Индерский район" },
    { region_id: 34, region_name: "Макатский район" },
    { region_id: 35, region_name: "Макатский район" },
    { region_id: 36, region_name: "Исатайский район" },
    { region_id: 37, region_name: "Атырауская область" },
    { region_id: 38, region_name: "Область Жетiсу" },
    { region_id: 39, region_name: "Индерский район" },
    { region_id: 40, region_name: "Индерский район" },
    { region_id: 41, region_name: "Макатский район" },
    { region_id: 42, region_name: "Кызылкогинский район" },
    { region_id: 43, region_name: "Макатский район" },
  ];

  // Строим URL
  const url = useMemo(() => {
    let time_range = "year";
    let urlStr = "/erap/sequence/overall_summary_by_organization?";

    if (!period || period === "month") {
      time_range = "month";
    } else if (period === "quarter") {
      time_range = "quarter";
    } else if (period === "year") {
      time_range = "year";
      const yearParam = new Date().getFullYear();
      urlStr += `year=${yearParam}&`;
    }

    urlStr += `time_range=${time_range}`;

    if (region && region !== "Вся область") {
      const selectedRegion = regionsList.find((r) => r.region_name === region);
      if (selectedRegion) {
        urlStr += `&region_id=${selectedRegion.region_id}`;
      }
    }

    return urlStr;
  }, [period, region]);

  const { data: response, isLoading: loading } = useApiRequest<OrgAnalyticsResponse>(url, [period, region]);

  /*
   * Демо-набор, когда ручки нет.
   *
   * `/erap/sequence/overall_summary_by_organization` у бэкенда Атырау не
   * существует (404), и блок стоял с нулями во всех плитках и пустой таблицей.
   * Разбивку по типам субъектов зеркало дать не может: фильтр `subjectType` у
   * реестра субъектов не принимается. Поэтому здесь заглушка — но привязанная к
   * настоящему числу протоколов, чтобы не спорить с плиткой «Всего нарушений».
   */
  const { data: erap } = useErapViolations();

  useEffect(() => {
    if (response) return;
    const total = erap.protocols?.total;
    if (!total) return;

    setOrgGroupsStats(buildOrgGroupRows(total));

    const totals = buildSubjectTotals(total);
    const asKpi = (value: number) => ({
      value,
      previous: 0,
      direction: "na" as const,
      percent: null,
      absolute: 0,
    });
    setKpis({
      entrepreneurs: { month: asKpi(totals.entrepreneurs) },
      minors: { month: asKpi(totals.minors) },
      officials: { month: asKpi(totals.officials) },
      foreigners: { month: asKpi(totals.foreigners) },
    } as any);
  }, [response, erap]);

  // Обработка данных при получении ответа
  useEffect(() => {
    if (!response) return;

        // Маппим строки (без overall) в нужный формат
        const mapped = response.rows
          .filter((row: any) => row.organization_type !== "overall")
          .map((row: any) => {
            const chosen = row.chosen;
            return {
              name: ORG_TITLES[row.organization_type] ?? row.organization_type,
              total: chosen.total_registered,
              individuals: chosen.total_physical_citizens,
              legalEntities: chosen.total_legal_entities,
              entrepreneurs: chosen.total_enterpreneurs,
              minors: chosen.total_underages,
              officials: chosen.total_gov_officials,
              foreigners: chosen.total_foreign_citizens,
            };
          });

        setOrgGroupsStats(mapped);

        const overallRow = response.rows.find((r: any) => r.organization_type === "overall");
        const chosen = overallRow?.chosen ?? {};
        const previous = overallRow?.previous ?? {};

        const calc = (
          cur: number | undefined,
          prev: number | undefined
        ): Kpi & { previous: number } => {
          const c = Number(cur ?? 0);
          const p = Number(prev ?? 0);

          let direction: Kpi["direction"];
          let percent: number | null;
          let absolute: number;

          if (p === 0) {
            if (c === 0) {
              direction = "no-change";
              percent = 0;
              absolute = 0;
            } else {
              direction = "na";
              percent = null;
              absolute = c - p;
            }
          } else {
            const diff = c - p;
            percent = (diff / p) * 100;
            direction = diff === 0 ? "no-change" : diff > 0 ? "up" : "down";
            absolute = Math.abs(diff);
          }

          return { direction, percent: percent !== null ? Math.abs(Number(percent.toFixed(2))) : null, absolute, previous: p };
        };


        const computedKpis: Record<string, Record<PeriodKey, Kpi>> = {
          analyticsEntrepreneurs: {
            month: calc(chosen.total_enterpreneurs, previous.total_enterpreneurs),
            quarter: calc(chosen.total_enterpreneurs, previous.total_enterpreneurs),
            year: calc(chosen.total_enterpreneurs, previous.total_enterpreneurs),
          },
          analyticsMinors: {
            month: calc(chosen.total_underages, previous.total_underages),
            quarter: calc(chosen.total_underages, previous.total_underages),
            year: calc(chosen.total_underages, previous.total_underages),
          },
          analyticsOfficials: {
            month: calc(chosen.total_gov_officials, previous.total_gov_officials),
            quarter: calc(chosen.total_gov_officials, previous.total_gov_officials),
            year: calc(chosen.total_gov_officials, previous.total_gov_officials),
          },
          analyticsForeigners: {
            month: calc(chosen.total_foreign_citizens, previous.total_foreign_citizens),
            quarter: calc(chosen.total_foreign_citizens, previous.total_foreign_citizens),
            year: calc(chosen.total_foreign_citizens, previous.total_foreign_citizens),
          },
        };

        setKpis(computedKpis);
  }, [response]);

  const getPeriodText = () => {
    if (!period || period === "month") return "за месяц";
    if (period === "quarter") return "за квартал";
    return "за год";
  };

  const getKpiDeltaText = (metricKey: keyof typeof kpis) => {
    if (!kpis || !["month", "quarter", "year"].includes(period)) return "";
    const metric = (kpis as any)[metricKey]?.[period as PeriodKey] as
      | (Kpi & { previous: number })
      | undefined;
    if (!metric) return "";

    const periodText = getPeriodText();

    if (metric.direction === "na") {
      return `— изменение: ${metric.previous.toLocaleString(
        "ru-RU"
      )} (${periodText}, процент N/A)`;
    }

    if (metric.direction === "no-change") {
      return `— без изменений (${periodText})`;
    }

    const symbol = metric.direction === "down" ? "↓" : "↑";
    const pct = metric.percent !== null ? `${metric.percent}%` : "N/A";
    return `${symbol} ${pct} (${metric.previous.toLocaleString(
      "ru-RU"
    )}) ${periodText}`;
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle>
          Аналитика по органам, накладывающим административные нарушения
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Верхние карточки */}
          <div className="grid grid-cols-1 ipad:grid-cols-2 ipad-landscape:grid-cols-4 gap-3 ipad:gap-4 mb-4">
            {[
              {
                label: "Индивидуальные предприниматели",
                key: "entrepreneurs",
                kpiKey: "analyticsEntrepreneurs",
                index: 0,
              },
              {
                label: "Несовершеннолетние",
                key: "minors",
                kpiKey: "analyticsMinors",
                index: 1,
              },
              {
                label: "Должностные лица ГО (ст.30 КРКоАП)",
                key: "officials",
                kpiKey: "analyticsOfficials",
                index: 2,
              },
              {
                label: "Иностранные граждане",
                key: "foreigners",
                kpiKey: "analyticsForeigners",
                index: 3,
              },
            ].map((item) => {
              const value = orgGroupsStats.reduce(
                (sum, g) => sum + (g[item.key] || 0),
                0
              );
              const deltaTextValue = getKpiDeltaText(item.kpiKey as any);

              // Определяем направление тренда из KPI
              const metric = kpis?.[item.kpiKey]?.[period as PeriodKey] as
                | (Kpi & { previous: number })
                | undefined;
              const isPositive = metric?.direction === "down"; // Уменьшение = хорошо (зеленое)

              const numberColors = [
                "text-slate-700",
                "text-blue-700",
                "text-indigo-700",
                "text-violet-700",
                "text-purple-700",
                "text-fuchsia-700",
                "text-pink-700",
                "text-rose-700",
                "text-orange-700",
                "text-amber-700",
                "text-emerald-700",
                "text-teal-700",
              ];
              const numberColor =
                numberColors[item.index % numberColors.length];

              return (
                <Card
                  key={item.key}
                  className="rounded-2xl border border-slate-200 bg-slate-50 shadow-sm h-full relative"
                >
                  {metric &&
                    metric.direction !== "no-change" &&
                    metric.direction !== "na" && (
                      <div className="absolute top-3 right-3">
                        {isPositive ? (
                          <TrendingDown size={18} className="text-green-600" />
                        ) : (
                          <TrendingUp size={18} className="text-red-600" />
                        )}
                      </div>
                    )}
                  <CardContent className="p-4 ipad:p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-1">
                          {item.label}
                        </div>
                        <div
                          className={`text-4xl font-bold ${numberColor} mb-1`}
                        >
                          {value.toLocaleString("ru-RU")}
                        </div>
                        {deltaTextValue && (
                          <div
                            className={`text-xs font-semibold mt-1 ${
                              isPositive ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            <span>{deltaTextValue}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Таблица */}
          <div className="overflow-x-auto -mx-4 ipad:mx-0">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2 ipad:p-3 font-semibold text-gray-700">
                    Группа органов
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Всего взысканий
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Физические лица
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Юридические лица
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Индивидуальные предприниматели
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Несовершеннолетние
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Должностные лица ГО
                  </th>
                  <th className="text-right p-2 ipad:p-3 font-semibold text-gray-700">
                    Иностранные граждане
                  </th>
                </tr>
              </thead>
              <tbody>
                {orgGroupsStats.map((group, index) => (
                  <tr
                    key={group.name}
                    className={`border-b ${
                      index % 2 === 1 ? 'bg-blue-50' : 'bg-white'
                    } hover:bg-blue-100`}
                  >
                    <td className="p-3">{group.name}</td>
                    <td className="text-right p-3">
                      {group.total.toLocaleString("ru-RU")}
                    </td>
                    <td className="text-right p-3">
                      {group.individuals.toLocaleString("ru-RU")}
                    </td>
                    <td className="text-right p-3">
                      {group.legalEntities.toLocaleString("ru-RU")}
                    </td>
                    <td className="text-right p-3 text-red-600">
                      {group.entrepreneurs.toLocaleString("ru-RU")}
                    </td>
                    <td className="text-right p-3 text-red-600">
                      {group.minors.toLocaleString("ru-RU")}
                    </td>
                    <td className="text-right p-3 text-red-600">
                      {group.officials.toLocaleString("ru-RU")}
                    </td>
                    <td className="text-right p-3 text-red-600">
                      {group.foreigners.toLocaleString("ru-RU")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrgAnalytics;
